import { cn } from '@commenda/utils';
import { XIcon } from 'lucide-react';
import * as React from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../internal/Command';
import { Badge } from '../Badge';
import { Checkbox } from '../Checkbox';
import { Icons } from '../Icons';
import { LoadingIndicator } from '../LoadingIndicator';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Separator } from '../Separator';

interface AsyncOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface AsyncMultiSelectProps {
  /** The current server-side search / page results. Treated as already filtered. */
  options: AsyncOption[];
  defaultValue: string[];
  onValueChange: (value: string[]) => void;
  /** Called (debounced) with the typed query so the consumer can refetch from the server. */
  onSearchChange: (query: string) => void;
  /** First/active query is loading (no results yet) — shows a spinner in place of the list. */
  isLoading?: boolean;
  /** A subsequent page is being fetched — shows a spinner at the bottom of the list. */
  isLoadingMore?: boolean;
  /** Whether more pages are available. */
  hasMore?: boolean;
  /** Requests the next page; called when the list is scrolled near the bottom. */
  onLoadMore?: () => void;
  placeholder?: string;
  searchText?: string;
  notFoundText?: string;
  maxCount?: number;
  maxSelectable?: number | null;
  hideClearAll?: boolean;
  error?: boolean;
  disabled?: boolean;
  id?: string;
  debounceMs?: number;
}

const AsyncMultiSelect = React.forwardRef<
  HTMLButtonElement,
  AsyncMultiSelectProps
>(
  (
    {
      options,
      defaultValue = [],
      onValueChange,
      onSearchChange,
      isLoading = false,
      isLoadingMore = false,
      hasMore = false,
      onLoadMore,
      placeholder = 'Select options',
      searchText = 'Search...',
      notFoundText = 'No results.',
      maxCount = 3,
      maxSelectable = null,
      hideClearAll = false,
      error = false,
      disabled,
      id,
      debounceMs = 250,
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    // Labels for every option ever seen, keyed by value. `options` only holds the current
    // result set, so a selected item can fall out of it; this keeps its tag/label resolvable.
    const [seenOptionsByValue, setSeenOptionsByValue] = React.useState<
      Record<string, AsyncOption>
    >({});

    React.useEffect(() => {
      setSelectedValues(defaultValue);
    }, [defaultValue]);

    // Forward the (debounced) query to the consumer for server-side search.
    React.useEffect(() => {
      const handle = setTimeout(() => {
        onSearchChange(searchQuery);
      }, debounceMs);
      return () => clearTimeout(handle);
    }, [searchQuery, onSearchChange, debounceMs]);

    // Accumulate seen options so selected labels survive after an item leaves `options`.
    React.useEffect(() => {
      if (!options.length) return;
      setSeenOptionsByValue((prev) => {
        let changed = false;
        const next = { ...prev };
        options.forEach((option) => {
          if (next[option.value]?.label !== option.label) {
            next[option.value] = option;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, [options]);

    const resolveOption = React.useCallback(
      (value: string): AsyncOption | undefined =>
        options.find((option) => option.value === value) ??
        seenOptionsByValue[value],
      [options, seenOptionsByValue]
    );

    // Pin the currently selected items (resolved from the cache) to the top so they stay
    // visible during a search, then the rest of the server result set.
    const optionsToRender = React.useMemo(() => {
      const selectedSet = new Set(selectedValues);
      const selectedResolved = selectedValues
        .map((value) => resolveOption(value))
        .filter((option): option is AsyncOption => !!option);
      const unselected = options.filter(
        (option) => !selectedSet.has(option.value)
      );
      return [...selectedResolved, ...unselected];
    }, [options, selectedValues, resolveOption]);

    const toggleOption = (value: string) => {
      const option = resolveOption(value);
      if (option?.disabled) return;
      if (
        maxSelectable &&
        selectedValues.length >= maxSelectable &&
        !selectedValues.includes(value)
      ) {
        return;
      }
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
      if (!onLoadMore || !hasMore || isLoadingMore) return;
      const el = event.currentTarget;
      const LOAD_MORE_THRESHOLD_PX = 80;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) {
        onLoadMore();
      }
    };

    const handlePopoverOpenChange = (open: boolean) => {
      if (!open) {
        setSearchQuery('');
      }
      setIsPopoverOpen(open);
    };

    return (
      <Popover open={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            ref={ref}
            id={id}
            onClick={() => setIsPopoverOpen(true)}
            className={cn(
              'flex w-full box-border items-center justify-between rounded-md px-xl bg-bg-primary text-text-primary txt-small-regular ring-0 transition-colors [&>span]:line-clamp-1',
              selectedValues.length > 0 ? 'min-h-10 py-1' : 'h-10 py-2.5',
              'disabled:cursor-not-allowed disabled:bg-bg-disabled-subtle disabled:text-text-disabled disabled:pointer-events-none appearance-none focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--color-bg-brand-hover)] focus-visible:border-fg-brand',
              'border border-border-primary hover:border-border-primary-hover hover:bg-bg-primary disabled:border-border-disabled',
              // Open state hides the border so it doesn't double up with the ring.
              isPopoverOpen &&
                'ring-4 ring-[var(--color-bg-brand-hover)] border-fg-brand',
              error
                ? 'border-border-error hover:border-border-error focus-visible:border-border-error-hover focus-visible:ring-border-error-hover'
                : ''
            )}
            disabled={disabled}
          >
            {selectedValues.length > 0 ? (
              <div className="flex justify-between align-middle items-stretch w-full">
                <div className="flex flex-wrap gap-2 items-center">
                  {selectedValues.slice(0, maxCount).map((value) => {
                    const option = resolveOption(value);
                    return (
                      <Badge
                        key={value}
                        closeIcon={{
                          isShown: !option?.disabled,
                          onClose: (e) => {
                            e.stopPropagation();
                            toggleOption(value);
                          },
                        }}
                        className="txt-small-medium text-text-secondary"
                      >
                        {option?.label ?? value}
                      </Badge>
                    );
                  })}
                  {selectedValues.length > maxCount && (
                    <Badge
                      closeIcon={{
                        isShown: !hideClearAll,
                        onClose: (e) => {
                          e.stopPropagation();
                          clearExtraOptions();
                        },
                      }}
                      className="txt-small-medium text-text-secondary"
                    >
                      {`+ ${selectedValues.length - maxCount} more`}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between align-middle self-stretch">
                  {!hideClearAll && (
                    <XIcon
                      className="size-4 mx-2 cursor-pointer text-fg-quaternary hover:text-fg-quaternary-hover"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClear();
                      }}
                    />
                  )}
                  {!hideClearAll && (
                    <Separator
                      orientation="vertical"
                      className="flex min-h-6 h-full"
                    />
                  )}
                  <Icons.ChevronDown className="ml-1 h-5 w-5 shrink-0 text-fg-quinary hover:text-fg-quinary-hover cursor-pointer" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="txt-medium-regular text-text-placeholder">
                  {placeholder}
                </span>
                <Icons.ChevronDown className="ml-1 h-5 w-5 shrink-0 text-fg-quinary hover:text-fg-quinary-hover cursor-pointer" />
              </div>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full p-0 min-w-[300px]"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
          style={{
            width: 'var(--radix-popper-anchor-width)',
          }}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={searchText}
              onKeyDown={handleInputKeyDown}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList
              onScroll={handleListScroll}
              className="max-h-[400px] overflow-y-auto overflow-x-hidden"
            >
              <CommandEmpty>
                {isLoading ? (
                  <div className="flex justify-center py-2.5">
                    <LoadingIndicator size={20} />
                  </div>
                ) : (
                  notFoundText
                )}
              </CommandEmpty>
              <CommandGroup>
                {optionsToRender.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      className={cn(
                        'gap-lg rounded-none hover:bg-bg-primary-hover',
                        option.disabled &&
                          'cursor-not-allowed pointer-events-none'
                      )}
                      {...(option.disabled ? { tabIndex: -1 } : {})}
                      data-disabled={option.disabled}
                      value={option.value}
                    >
                      <div className="flex items-center gap-lg">
                        <Checkbox size="sm" checked={isSelected} />
                        <span>{option.label}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {isLoadingMore ? (
                <div className="flex justify-center py-2.5">
                  <LoadingIndicator size={20} />
                </div>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

AsyncMultiSelect.displayName = 'AsyncMultiSelect';

export { AsyncMultiSelect };
